/**
 * Analytics taxonomy - single source of truth for the frontend.
 *
 * Every PostHog event name and property key in the app should resolve to a
 * constant exported here. The literal strings are preserved exactly as they fire today so existing PostHog
 * insights keep working. Renaming to the snake_case convention happens in a
 * later phase.
 */

// --- Origination taxonomy -------------------------------------------------

export const ANALYTICS_EVENTS_KEY = {
    CTA_BUTTON_PAGE: 'CTA Button Page',
    OBJECT_CLICKED: 'Object Clicked',
    /** ISO date (YYYY-MM-DD) for payment schedule date picks */
    PAYMENT_DATE: 'Payment Date',
    /** RepaymentMonthlyEnum value when a monthly preset row is chosen */
    MONTHLY_PRESET: 'Monthly Preset',
    SLIDER_NAME: 'Slider Name',
    SLIDER_VALUE: 'Slider Value',
    ONFIDO_EVENT_NAME: 'Onfido Event Name',
    ONFIDO_EVENT_PROPERTIES: 'Onfido Event Properties',
    DROPDOWN_NAME: 'Dropdown Name',
    DROPDOWN_VALUE: 'Dropdown Value'
} as const

export const ANALYTICS_OBJECTS = {
    LOAN_AMOUNT_EDIT_PENCIL_ICON: 'Loan Amount Edit Pencil Icon',
    LOAN_PURPOSE_DROPDOWN: 'Loan Purpose Dropdown',
    PROVINCE_DROPDOWN: 'Province Dropdown',
    CTA_BUTTON: 'CTA Button',
    BACK_BUTTON: 'Back Button',
    TOOLTIP_ICON: 'Tooltip Icon',
    LANGUAGE_SWITCHER: 'Language Switcher',
    PAYMENT_FREQUENCY_SELECTOR: 'Payment Frequency Selector',
    PAYMENT_DATE_SELECTOR: 'Payment Date Selector',
    PAYMENT_PROTECTION: {
        ACCORDION: {
            FIRST: 'First Accordion',
            SECOND: 'Second Accordion'
        },
        TERMS_AND_FAQ: {
            TERMS_LINK: 'Terms Link',
            FAQ_LINK: 'FAQ Link',
            FAQ_ACCORDION: 'FAQ Accordion'
        },
        CHECKBOX: {
            ACCEPT_CHECKBOX: 'Accept Checkbox',
            DECLINE_CHECKBOX: 'Decline Checkbox'
        }
    }
} as const

export const ANALYTICS_EVENTS = {
    CLICKED: 'Clicked',
    CTA_BUTTON_CLICKED: 'CTA Button Clicked',
    PAGE_VIEWED: 'Page Viewed',
    SLIDER_CHANGED: 'Slider Changed',
    ONFIDO_EVENT: 'Onfido Event',
    DROPDOWN_SELECTED: 'Dropdown Selected'
} as const

export const FUNNEL_EVENTS = {
    OTP_SENT: 'otp_sent',
    OTP_VERIFIED: 'otp_verified',
    OTP_FAILED: 'otp_failed',
    ONFIDO_STEP_COMPLETED: 'onfido_step_completed'
} as const

export const FUNNEL_EVENT_PROPERTY_KEYS = {
    APPLICATION_ID: 'application_id',
    IS_RESEND: 'is_resend',
    REASON: 'reason',
    EVENT_TYPE: 'event_type',
    STEP_NAME: 'step_name',
    ERROR_CODE: 'error_code'
} as const

/** Controlled failure reasons for otp_failed (no PII). */
export const OTP_FAILURE_REASONS = {
    INCORRECT_CODE: 'incorrect_code',
    EXPIRED_CODE: 'expired_code',
    RATE_LIMITED: 'rate_limited',
    TOO_MANY_ATTEMPTS: 'too_many_attempts',
    NO_SIGN_IN: 'no_sign_in',
    UNKNOWN: 'unknown',
    SEND_FAILED: 'send_failed',
    RESEND_FAILED: 'resend_failed'
} as const

export const SCREEN_NAMES = {
    error: 'Error screen',
    'loan-amount': 'Prequal: Loan Amount screen',
    'about-yourself': 'Prequal: About Yourself screen',
    'personal-info': 'Prequal: Personal Info screen',
    'employment-info': 'Prequal: Employment Info screen',
    'financial-info': 'Prequal: Financial Info screen',
    'housing-status': 'Prequal: Housing Status screen',
    'affiliate-offer': 'Affiliate Offer screen',
    'embedded-offer': 'Embedded Offer screen',
    declined: 'Prequal: Declined screen',
    'customize-your-offer': 'Personal Loan: Customize Your Offer screen',
    'phone-number': 'Personal Loan: Phone Number capture screen',
    'kyc-verify': 'Personal Loan: KYC Verify screen',
    'connect-bank-account': 'Personal Loan: Connect Bank Account screen',
    'flinks-connect': 'Personal Loan: Flinks Connect screen',
    'confirm-payment-schedule': 'Personal Loan: Confirm Payment Schedule screen',
    'edit-payment-schedule': 'Personal Loan: Edit Payment Schedule screen',
    'payment-protection': 'Personal Loan: Payment Protection screen',
    review: 'Personal Loan: Review screen',
    finish: 'Personal Loan: Finish screen'
} as const

// --- Portal taxonomy ------------------------------------------------------

/**
 * Portal analytics constants for PostHog event tracking.
 *
 * Event capture is done via usePortalTracking hook (app/portal/hooks/usePortalTracking.ts).
 * These constants provide consistent event names and property values across portal components.
 */
export const PORTAL_EVENT_NAMES = {
    PAYMENT_DELAY_PAYMENT_SCHEDULE_SELECT_BUTTON_CLICKED: 'payment_delay_payment_schedule_select',
    PAYMENT_SCHEDULE_CHANGE_SELECT_BUTTON_CLICKED: 'payment_schedule_change_select',
    PAYMENT_SCHEDULE_CHANGE_SELECT_ERROR_TRIGGERED: 'payment_schedule_change_select_error',
    PAYMENT_SCHEDULE_CHANGE_CONFIRM_COMPLETE_BUTTON_CLICKED: 'payment_schedule_change_confirm_complete',
    PAYMENT_SCHEDULE_CHANGE_CONFIRM_FILE_DOWNLOADED: 'payment_schedule_change_confirm_file',
    PAYMENT_SCHEDULE_CONFIRM_ERROR_TRIGGERED: 'payment_schedule_confirm_error',
    PAYMENT_SCHEDULE_CHANGE_EDIT_ERROR_TRIGGERED: 'payment_schedule_change_edit_error',
    PAYMENT_SCHEDULE_CHANGE_EDIT_CONTINUE_BUTTON_CLICKED: 'payment_schedule_change_edit_continue',
    PROFILE_UPDATE_SUBMIT_BUTTON_CLICKED: 'profile_update_submit'
} as const

export const ANALYTICS_OBJECT_CONSTANTS = { BUTTON: 'button', API: 'api', FILE: 'file' } as const

export const ANALYTICS_ACTION_CONSTANTS = {
    CLICKED: 'clicked',
    RESPONSE: 'response',
    DOWNLOADED: 'downloaded'
} as const

// --- Groups ---------------------------------------------------------------

/** PostHog group type used for partner-level funnel breakdowns (see strategy section 6.3). */
export const PARTNER_GROUP_TYPE = 'partner' as const

// --- Types ----------------------------------------------------------------

export type OriginationEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]
export type PortalEventName = (typeof PORTAL_EVENT_NAMES)[keyof typeof PORTAL_EVENT_NAMES]
export type FunnelEventName = (typeof FUNNEL_EVENTS)[keyof typeof FUNNEL_EVENTS]

/** Union of all canonical (non-interpolated) event names known to the app. */
export type AnalyticsEvent = OriginationEventName | PortalEventName | FunnelEventName

export type AnalyticsEventPropertyKey = (typeof ANALYTICS_EVENTS_KEY)[keyof typeof ANALYTICS_EVENTS_KEY]
export type FunnelEventPropertyKey = (typeof FUNNEL_EVENT_PROPERTY_KEYS)[keyof typeof FUNNEL_EVENT_PROPERTY_KEYS]
export type OtpFailureReason = (typeof OTP_FAILURE_REASONS)[keyof typeof OTP_FAILURE_REASONS]
export type ScreenName = (typeof SCREEN_NAMES)[keyof typeof SCREEN_NAMES]

export type OtpSentProperties = {
    [FUNNEL_EVENT_PROPERTY_KEYS.APPLICATION_ID]: string
    [FUNNEL_EVENT_PROPERTY_KEYS.IS_RESEND]?: boolean
}

export type OtpVerifiedProperties = {
    [FUNNEL_EVENT_PROPERTY_KEYS.APPLICATION_ID]: string
}

export type OtpFailedProperties = {
    [FUNNEL_EVENT_PROPERTY_KEYS.APPLICATION_ID]: string
    [FUNNEL_EVENT_PROPERTY_KEYS.REASON]: OtpFailureReason
    [FUNNEL_EVENT_PROPERTY_KEYS.IS_RESEND]?: boolean
    [FUNNEL_EVENT_PROPERTY_KEYS.ERROR_CODE]?: string
}

export type OnfidoStepCompletedProperties = {
    [FUNNEL_EVENT_PROPERTY_KEYS.APPLICATION_ID]: string
    [FUNNEL_EVENT_PROPERTY_KEYS.EVENT_TYPE]: string
    [FUNNEL_EVENT_PROPERTY_KEYS.STEP_NAME]: string
    [FUNNEL_EVENT_PROPERTY_KEYS.ERROR_CODE]?: string
}

export type FunnelEventProperties = OtpSentProperties | OtpVerifiedProperties | OtpFailedProperties | OnfidoStepCompletedProperties
